import React from 'react'
import parse from "html-react-parser";
import { Pencil, Trash2, Clock, Plus } from "lucide-react";
import axios from "axios";
import { useEditor } from "../../../../App.jsx";
import { confirmDelete, showAlert } from "../../../../components/HelperAlerts/HelpersAlerts.jsx";
import { getThemeContext } from "../../../../components/providers/ThemeProvider.jsx";
import { capitalize } from "../../../../components/tools/utils.jsx";

// Con `onMedioSelect` la tarjeta entra en modo "insertar" (explorador del
// customiz3r): doble clic / botón insertan y se ocultan editar/borrar.
const MedioCardList = ({ media, setEditor, setEditorType, setEditorID, onDelete, onMedioSelect }) => {
    const theme = getThemeContext();
    const { stateEditor, setStateEditor } = useEditor();

    const handleEdit = () => {
        setStateEditor({
            ...stateEditor,
            editor: true,
            editorID: media.id,
            editorType: "medios_svg_input"
        });
    };

    const handleDelete = async () => {
        const confirmed = await confirmDelete({
            title: `¿Eliminar "${media.nombre}"?`,
            text: "Esta acción no se puede deshacer.",
            confirmButtonText: "Eliminar",
            theme,
        });
        if (confirmed) {
            try {
                await axios.delete(`/media/${media.id}/`);
                if (onDelete) onDelete(media.id);
                showAlert({ title: "Eliminado", icon: "success", timer: 1500, showConfirmButton: false, theme });
            } catch (error) {
                console.error('Error al eliminar el medio:', error);
                showAlert({ title: "Error", text: "No se pudo eliminar el medio.", icon: "error", theme });
            }
        }
    };

    function removeWidthAndHeightAttributes(svgString) {
        return svgString.replace(/width="[^"]*"/, '').replace(/height="[^"]*"/, '');
    }

    return (
        <div
            className="flex items-stretch bg-base-100 rounded-xl
                        shadow-md border border-base-200
                        hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/30
                        transition-all duration-200 overflow-hidden h-28
                        cursor-pointer"
            onDoubleClick={onMedioSelect ? () => onMedioSelect(media) : undefined}
            title={onMedioSelect ? "Doble clic para insertar" : undefined}
        >

            {/* Accent stripe */}
            <div className="w-1 shrink-0 bg-primary" />

            {/* SVG thumbnail */}
            <div className="w-28 h-full shrink-0 svg-checker flex items-center justify-center p-2">
                <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain">
                    {parse(
                        removeWidthAndHeightAttributes(media.svg).replace(
                            '<svg',
                            '<svg class="w-full h-full object-contain"'
                        )
                    )}
                </div>
            </div>

            {/* Divider */}
            <div className="w-px bg-base-200 shrink-0 my-3" />

            {/* Info */}
            <div className="flex-1 flex flex-col justify-center px-4 min-w-0 gap-1">
                <h3 className="font-semibold text-base-content text-base leading-snug truncate"
                    title={capitalize(media.nombre)}>
                    {capitalize(media.nombre)}
                </h3>
                {media.created_at && (
                    <p className="flex items-center gap-1.5 text-xs text-base-content/40">
                        <Clock className="w-3 h-3 shrink-0" />
                        {new Date(media.created_at).toLocaleDateString('es-ES', {
                            day: '2-digit', month: 'short', year: 'numeric'
                        })}
                    </p>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 px-4 shrink-0">
                {onMedioSelect ? (
                    <button
                        className="btn btn-primary btn-sm gap-1.5"
                        onClick={() => onMedioSelect(media)}
                        title="Insertar"
                    >
                        <Plus className="w-4 h-4" />
                        Insertar
                    </button>
                ) : (
                    <>
                        <button
                            className="btn btn-ghost btn-sm btn-square text-base-content/60 hover:text-primary hover:bg-primary/10"
                            onClick={handleEdit}
                            title="Editar"
                        >
                            <Pencil className="w-4 h-4" />
                        </button>
                        <button
                            className="btn btn-ghost btn-sm btn-square text-base-content/60 hover:text-error hover:bg-error/10"
                            onClick={handleDelete}
                            title="Eliminar"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default MedioCardList;
